import {
  Box,
  Chevron,
  Disclaimer,
  Div,
  H5,
  Icon,
  RestaurantReferral,
  RestaurantReferralSide,
  TooltipWrapper,
  TruncatedText,
  lightTheme,
  RestaurantReferralStatusPill,
} from '@vgrubs/components';
import { useNavigate } from 'react-router';
import { formatReferralDay } from '../helpers/referralLabels';

/** Widths shared with the list's TableHeader, so the columns line up. */
export const REFERRAL_COLUMNS = [
  { label: 'New Restaurant', width: '26%' },
  { label: 'Referred By', width: '26%' },
  { label: 'Status', width: '16%' },
  { label: 'Signed Up', width: '13%' },
  { label: 'Qualify By', width: '13%' },
  { label: '', width: '2%' },
];

type Props = {
  referral: RestaurantReferral;
  onOpen: (referralId: number) => void;
};

/**
 * One referral: who came in, who brought them, and where it stands. Columns
 * on desktop; on a phone the same facts as a card, the status pill top right.
 */
export const ReferralRow = ({ referral, onOpen }: Props) => {
  const { referred, referrer } = referral;

  return (
    <Box
      $mb="8"
      $flex
      $alignCenter
      $gap="32px"
      $pointer
      onClick={() => onOpen(referral.id)}
    >
      <Div $desktop $width={REFERRAL_COLUMNS[0].width} $minWidth="0">
        <RestaurantCell side={referred} />
      </Div>
      <Div $desktop $width={REFERRAL_COLUMNS[1].width} $minWidth="0">
        <RestaurantCell side={referrer} />
      </Div>
      <Div $desktop $width={REFERRAL_COLUMNS[2].width}>
        <RestaurantReferralStatusPill status={referral.status} />
      </Div>
      <Div $desktop $width={REFERRAL_COLUMNS[3].width}>
        <Disclaimer $m="0">{formatReferralDay(referral.signedUpAt)}</Disclaimer>
      </Div>
      <Div $desktop $width={REFERRAL_COLUMNS[4].width}>
        <Disclaimer $m="0">{formatReferralDay(referral.qualifyBy)}</Disclaimer>
      </Div>
      <Div $desktop $width={REFERRAL_COLUMNS[5].width} $flex $justifyEnd>
        <Icon $rotate="90deg">
          <Chevron />
        </Icon>
      </Div>

      <Div $mobile $width="100%" $flex $column $gap="8px">
        <Div $flex $between $alignStart $gap="12px">
          <Div $minWidth="0">
            <RestaurantCell side={referred} />
          </Div>
          <RestaurantReferralStatusPill status={referral.status} />
        </Div>
        <Disclaimer $m="0">Referred by {referrer.name}</Disclaimer>
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          Signed up {formatReferralDay(referral.signedUpAt)}
          {referral.qualifyBy &&
            ` · Qualify by ${formatReferralDay(referral.qualifyBy)}`}
        </Disclaimer>
      </Div>
    </Box>
  );
};

/**
 * A restaurant over its owner, both cut to one line with the whole in a
 * tooltip. The name opens that location's details, the way a location name
 * does on the Outstanding Balances list; the rest of the row keeps opening
 * the referral.
 */
const RestaurantCell = ({ side }: { side: RestaurantReferralSide }) => {
  const navigate = useNavigate();

  return (
    <Div $flex $column $gap="2px" $minWidth="0">
      <Div
        $pointer
        $minWidth="0"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          navigate(`/locations/${side.locationId}`);
        }}
      >
        <TooltipWrapper tooltip={side.name}>
          <H5 $m="0">
            <TruncatedText underline>{side.name}</TruncatedText>
          </H5>
        </TooltipWrapper>
      </Div>
      {side.ownerName && (
        <Disclaimer $m="0">
          <TruncatedText>{side.ownerName}</TruncatedText>
        </Disclaimer>
      )}
    </Div>
  );
};
